import { useTranslation } from 'react-i18next';
import { categoryLabel, categoryOptionLabel } from '@/lib/category';
import { useState, useEffect, useCallback, useMemo, type ReactNode } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import { zhCN, enUS } from "date-fns/locale";
import { fetchAllEvents, fetchCategoryOptions } from "@/services/api";
import type { CategoryOption, Event } from "@/types";
import { addDays, formatDateOnlyMonthDay, tryParseDateOnly } from "@/lib/date";
import { ExternalLink } from "lucide-react";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "./CalendarStyles.css";

const locales = {
  "zh-CN": zhCN,
  en: enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// 分类色只作为事件条左侧的 3px 色线和图例，文字始终说明分类。
const categoryColors: Record<string, string> = {
  aircraft: '#2b5797',
  history: '#7a4ea8',
  industry: '#2e7d32',
  airshows: '#d84400',
};
const DEFAULT_COLOR = '#6b6b6b';
// 特定日期事件（如航展）沿用国际橙。
const SPECIFIC_COLOR = '#d84400';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: Event;
  occurrenceYear?: number;
}

function convertToCalendarEvents(
  events: Event[],
  visibleYear: number,
  formatAnniversary: (title: string, count: number) => string,
): CalendarEvent[] {
  const calendarEvents: CalendarEvent[] = [];
  const annualOccurrenceYears = [visibleYear - 1, visibleYear, visibleYear + 1];

  for (const event of events) {
    if (event.event_type === "specific" && event.start_date) {
      // 特定日期范围事件
      const start = tryParseDateOnly(event.start_date);
      if (!start) {
        continue;
      }
      let end: Date;

      if (event.end_date) {
        // 有结束日期，使用结束日期
        // react-big-calendar 的 end 是独占的，所以需要加一天
        const inclusiveEnd = tryParseDateOnly(event.end_date);
        if (!inclusiveEnd || inclusiveEnd < start) {
          continue;
        }
        end = addDays(inclusiveEnd, 1);
      } else {
        // 单日事件
        end = addDays(start, 1);
      }

      calendarEvents.push({
        id: `${event.uid}:specific`,
        title: event.title,
        start,
        end,
        resource: event,
      });
    } else if (event.event_type === "annual" && event.month && event.day) {
      for (const occurrenceYear of annualOccurrenceYears) {
        const start: Date = new Date(occurrenceYear, event.month - 1, event.day);

        // Date 会自动归一化非法日期；这里跳过非闰年的 2 月 29 日等无效日期。
        if (
          start.getFullYear() !== occurrenceYear ||
          start.getMonth() !== event.month - 1 ||
          start.getDate() !== event.day
        ) {
          continue;
        }

        const end = addDays(start, 1);
        let title = event.title;
        if (event.year) {
          const anniversary = occurrenceYear - event.year;
          if (anniversary > 0) {
            title = formatAnniversary(event.title, anniversary);
          }
        }

        calendarEvents.push({
          id: `${event.uid}:annual:${occurrenceYear}`,
          title,
          start,
          end,
          resource: event,
          occurrenceYear,
        });
      }
    }
  }

  return calendarEvents;
}

function eventColor(event: Event): string {
  if (event.event_type === "specific") {
    return SPECIFIC_COLOR;
  }
  if (event.category_slug && categoryColors[event.category_slug]) {
    return categoryColors[event.category_slug];
  }
  return DEFAULT_COLOR;
}

interface EventDetailProps {
  calendarEvent: CalendarEvent;
  onClose: () => void;
}

function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/* Event detail: a paper panel with a 2px ink rule, serif title and hairline data rows. */
function EventDetail({ calendarEvent, onClose }: EventDetailProps) {
  const { t, i18n } = useTranslation('public');
  const formatDate = (value: string) => {
    const parsed = tryParseDateOnly(value);
    if (!parsed) {
      return value;
    }
    return new Intl.DateTimeFormat(i18n.resolvedLanguage, { dateStyle: 'medium' }).format(parsed);
  };
  const { resource: event, occurrenceYear } = calendarEvent;
  let anniversary: number | null = null;
  if (event.event_type === "annual" && event.year && occurrenceYear) {
    anniversary = occurrenceYear - event.year;
  }

  useEffect(() => {
    const handleKey = (keyboardEvent: KeyboardEvent) => {
      if (keyboardEvent.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  let titleContent: ReactNode = event.title;
  if (event.url) {
    titleContent = (
      <a
        href={event.url}
        target="_blank"
        rel="noopener noreferrer"
        className="transition-colors hover:text-accent-text"
      >
        {event.title}
      </a>
    );
  }

  let dateText = '';
  if (event.event_type === "specific" && event.start_date) {
    // 特定日期范围事件
    dateText = formatDate(event.start_date);
    if (event.end_date) {
      dateText = `${dateText} – ${formatDate(event.end_date)}`;
    }
  } else if (event.month && event.day) {
    // 年度事件
    dateText = formatDateOnlyMonthDay(`2000-${String(event.month).padStart(2, '0')}-${String(event.day).padStart(2, '0')}`);
  }

  let yearText = '';
  if (event.year) {
    yearText = t('year', { year: event.year });
    if (anniversary !== null && anniversary > 0) {
      yearText = `${yearText} · ${t('anniversary', { count: anniversary })}`;
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="calendar-event-title"
        className="w-full max-w-md border border-rule border-t-2 border-t-ink bg-paper p-6"
        onClick={(clickEvent) => clickEvent.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b border-rule pb-3">
          <h3 id="calendar-event-title" className="m-0 text-[1.375rem] leading-tight">{titleContent}</h3>
          <button type="button" className="text-link shrink-0 leading-6" onClick={onClose}>
            {t('close')}
          </button>
        </div>

        {event.description && (
          <p className="m-0 mt-3 text-sm leading-relaxed text-ink-2">{event.description}</p>
        )}

        <dl className="data-list m-0 mt-3">
          {dateText && <DetailRow label={t('detail.date')}><span className="font-mono text-[13px]">{dateText}</span></DetailRow>}
          {yearText && <DetailRow label={t('detail.year')}>{yearText}</DetailRow>}
          {event.category && <DetailRow label={t('detail.category')}>{categoryLabel(event)}</DetailRow>}
          {event.location && <DetailRow label={t('detail.location')}>{event.location}</DetailRow>}
          {event.tags && <DetailRow label={t('detail.tags')}>{event.tags}</DetailRow>}
        </dl>

        {event.url && (
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-link mt-4 inline-flex items-center gap-1.5"
          >
            {t('events.details')}
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  );
}

export function CalendarView() {
  const { t, i18n } = useTranslation('public');
  const culture = i18n.resolvedLanguage === 'en' ? 'en' : 'zh-CN';
  const [sourceEvents, setSourceEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  const [date, setDate] = useState(new Date());
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [reloadToken, setReloadToken] = useState(0);
  const visibleYear = date.getFullYear();
  const events = useMemo(
    () => convertToCalendarEvents(sourceEvents, visibleYear, (title, count) => t('anniversaryTitle', { title, count })),
    [sourceEvents, visibleYear, t],
  );

  // 加载分类列表
  useEffect(() => {
    const controller = new AbortController();

    fetchCategoryOptions(controller.signal)
      .then(setCategories)
      .catch((categoryError: unknown) => {
        if (!controller.signal.aborted) {
          console.error("Failed to fetch categories:", categoryError);
        }
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const loadEvents = async () => {
      setLoading(true);
      setError(false);

      try {
        const data = await fetchAllEvents(
          selectedCategory || null,
          controller.signal,
        );

        if (!controller.signal.aborted) {
          setSourceEvents(data);
        }
      } catch {
        if (!controller.signal.aborted) {
          setError(true);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    void loadEvents();
    return () => controller.abort();
  }, [reloadToken, selectedCategory]);

  const handleSelectEvent = useCallback((calEvent: CalendarEvent) => {
    setSelectedEvent(calEvent);
  }, []);

  const closeDetail = useCallback(() => {
    setSelectedEvent(null);
  }, []);

  // 事件条：纸色底、3px 分类色左线、墨色文字，没有圆角和填充色块。
  const eventStyleGetter = useCallback((event: CalendarEvent) => {
    return {
      style: {
        backgroundColor: "#efede8",
        borderLeft: `3px solid ${eventColor(event.resource)}`,
        borderRadius: 0,
        color: "#141414",
        fontSize: "12px",
      },
    };
  }, []);

  let calendarBody: ReactNode = (
    <p className="kicker m-0 py-12" aria-live="polite">{t('loading')}</p>
  );
  if (error) {
    calendarBody = (
      <div className="py-12">
        <p className="m-0 text-sm text-ink-2">{t('calendar.loadError')}</p>
        <button
          type="button"
          onClick={() => setReloadToken((token) => token + 1)}
          className="text-link mt-3"
        >
          {t('retry')}
        </button>
      </div>
    );
  } else if (!loading) {
    calendarBody = (
      <Calendar
        localizer={localizer}
        culture={culture}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 640 }}
        view="month"
        views={["month"]}
        date={date}
        onNavigate={setDate}
        onSelectEvent={handleSelectEvent}
        eventPropGetter={eventStyleGetter}
        popup
        messages={{
          today: t('calendar.today'),
          previous: t('calendar.previous'),
          next: t('calendar.next'),
          month: t('calendar.month'),
          date: t('calendar.date'),
          time: t('calendar.time'),
          event: t('calendar.event'),
          allDay: t('calendar.allDay'),
          noEventsInRange: t('calendar.empty'),
          showMore: (total: number) => t('calendar.showMore', { count: total }),
        }}
      />
    );
  }

  return (
    <div className="pt-4">
      {/* 分类筛选器 */}
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2 pb-4">
        <label htmlFor="calendar-category" className="kicker">{t('calendar.category')}</label>
        <select
          id="calendar-category"
          aria-label={t('calendar.category')}
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="select-underline min-w-[10rem]"
        >
          <option value="">{t('calendar.allCategories')}</option>
          {categories.map((category) => (
            <option key={category.slug} value={category.slug}>
              {categoryOptionLabel(category)}
            </option>
          ))}
        </select>
      </div>

      {/* 日历 */}
      <div aria-busy={loading}>{calendarBody}</div>

      {/* 图例沿用分类的数据库名称，语言切换不会改变分类标识。 */}
      <ul className="m-0 mt-4 flex list-none flex-wrap gap-x-5 gap-y-2 p-0 text-xs text-ink-2">
        {categories.map(category => (
          <li key={category.slug} className="flex items-center gap-2">
            <span
              className="inline-block h-3 w-[3px]"
              style={{ background: categoryColors[category.slug] ?? DEFAULT_COLOR }}
              aria-hidden="true"
            ></span>
            <span>{categoryOptionLabel(category)}</span>
          </li>
        ))}
        {sourceEvents.some(event => !event.category_slug) && (
          <li className="flex items-center gap-2">
            <span className="inline-block h-3 w-[3px]" style={{ background: DEFAULT_COLOR }} aria-hidden="true"></span>
            <span>{t('calendar.legend.other')}</span>
          </li>
        )}
      </ul>

      {/* 事件详情弹窗 */}
      {selectedEvent && (
        <EventDetail
          calendarEvent={selectedEvent}
          onClose={closeDetail}
        />
      )}
    </div>
  );
}
